/** Background Tasks — multitenant grid over `/v2/console/bgtasks/`.
 *
 * Console users see tasks of every tenant plus console-scoped (null-TO) ones.
 * Toolbar: tenant picker (incl. "Console only"), user picker (console +
 * dashboard staff — guests can't start tasks), status multi-select, started
 * from/to, search; auto-refresh lives in the grid toolbar's Refresh control
 * (on at 10s by default). Header filters (Title, User) feed the same
 * URL params as the toolbar. Cells expose quick actions via
 * CellActionsPopover (audit-grid conventions: local time + UTC popover,
 * range presets on timestamps). Row click opens the tabbed details dialog
 * (deep-linkable via /bgtasks/:taskId).
 */
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import HistoryIcon from '@mui/icons-material/History';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ShareIcon from '@mui/icons-material/Share';
import {
  Box,
  Checkbox,
  CircularProgress,
  IconButton,
  ListItemText,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  getGridSingleSelectOperators,
  getGridStringOperators,
  type GridColDef,
} from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import * as bgtasksApi from '@/api/bgtasks';
import { TASK_STATE, type BackgroundTaskRecord } from '@/api/bgtasks';
import { PAGE_FILL_SX } from '@/layout/pageFill';
import { pageTenantsFrom } from '@/scope/pageTenants';
import { formatTenantIds, tenantIdsToApiWith } from '@/scope/tenantIds';
import { useTenantScopeList } from '@/scope/tenantScope';
import { CellActionsPopover, type CellAction } from '@/components/DataGrid/CellActionsPopover';
import { HelpLink } from '@/components/HelpLink';
import { orderColumns } from '@/components/DataGrid/columnOrder';
import {
  ResourceDataGrid,
  type ResourceDataGridParams,
} from '@/components/DataGrid/ResourceDataGrid';
import { ResponsiveFilterBar, type FilterBarItem } from '@/components/DataGrid/ResponsiveFilterBar';
import { useGridAutoRefresh } from '@/components/DataGrid/useGridAutoRefresh';
import { useSnackbar } from '@/components/Snackbar';
import { WithTenantMark } from '@/components/TenantMark/TenantMark';
import {
  TenantPicker,
  pickedSentinel,
  type TenantPickerValue,
} from '@/components/TenantPicker/TenantPicker';
import { boundToParam, paramToBound, TimeWindowPicker } from '@/components/TimeWindowPicker';
import { UserPicker, type UserPickerValue } from '@/components/UserPicker/UserPicker';
import { useGridFilters, type FilterDef } from '@/hooks/useGridFilters';
import { makeResourceFiltersAdapter, useGridPresets } from '@/hooks/useGridPresets';
import { usePermissions } from '@/hooks/usePermissions';

import { TaskDetailsDialog } from './TaskDetailsDialog';
import { useBgTaskReadState } from './useBgTaskReadState';
import {
  TaskProgressBar,
  TaskStateChip,
  fmtTs,
  formatDuration,
  paramsEntries,
  paramsSummary,
  relativeTime,
  utcTs,
} from './taskFormat';

const STATE_OPTIONS = [
  { value: TASK_STATE.PENDING, label: 'Pending' },
  { value: TASK_STATE.RUNNING, label: 'Running' },
  { value: TASK_STATE.COMPLETED, label: 'Completed' },
  { value: TASK_STATE.FAILED, label: 'Failed' },
  { value: TASK_STATE.CANCELLED, label: 'Cancelled' },
  { value: TASK_STATE.AWAITING_APPROVAL, label: 'Awaiting approval' },
];

const DELTAS: { label: string; tooltip: string; ms: number }[] = [
  { label: '±5m', tooltip: '±5 minutes', ms: 5 * 60_000 },
  { label: '±1h', tooltip: '±1 hour', ms: 3_600_000 },
  { label: '±12h', tooltip: '±12 hours', ms: 12 * 3_600_000 },
  { label: '±24h', tooltip: '±24 hours', ms: 24 * 3_600_000 },
];

// The server matches these with icontains — offer only that operator.
const containsOnly = getGridStringOperators().filter((op) => op.value === 'contains');

/** Sentinel tenant option: console-scoped tasks (`tour_operator IS NULL`). */
const CONSOLE_TENANT: TenantPickerValue = { id: 0, name: 'Console only (no tenant)' };
const CONSOLE_EXTRA_OPTIONS = [CONSOLE_TENANT];

const VISIBILITY_OPTIONS = [
  { value: '0', label: 'Owner only' },
  { value: '1', label: 'Access group' },
  { value: '2', label: 'Tour operator' },
];

// The 'is' operator drives singleSelect header filters (exact-match params).
const isOperator = getGridSingleSelectOperators().filter((op) => op.value === 'is');

/** URL-backed filter dimensions. Header filters: task_type / state /
 *  visibility (singleSelect 'is') + user (user_email contains → `user`).
 *  Toolbar-only: tenant + started range. */
const FILTERS: FilterDef[] = [
  { param: 'task_label_contains', column: { field: 'task_label', operator: 'contains' } },
  { param: 'task_type', column: { field: 'task_type', operator: 'is' } },
  { param: 'state', column: { field: 'state', operator: 'is' } },
  { param: 'visibility', column: { field: 'visibility', operator: 'is' } },
  { param: 'user', column: { field: 'user_email', operator: 'contains' } },
  // Operator ids (`12,845`), or `null` alone: console-scoped tasks (sentinels
  // are exclusive, D3).
  { param: 'tour_operator_id', toApi: tenantIdsToApiWith(['null']), tenantScope: 'multi' },
  { param: 'started_from' },
  { param: 'started_to' },
];

/** Presets store the bgtasks filter state as `{params: {...}}` over the same
 *  URL params useGridFilters owns. Auto-refresh is deliberately NOT covered. */
const PRESET_FILTERS = makeResourceFiltersAdapter(FILTERS);

/** Column field order (mirrors the columns memo — keep in sync). */
const FIELD_ORDER = [
  'id',
  'tour_operator',
  'task_label',
  'task_type',
  'state',
  'visibility',
  'progress',
  'user_email',
  'started_ts',
  'created_ts',
  'params',
];

const DEFAULT_HIDDEN = ['params', 'created_ts', 'visibility', 'task_type', 'error_message', 'finished_ts', 'updated_ts'];

export function BgTasksPage() {
  const navigate = useNavigate();
  const { hasPerm } = usePermissions();
  const canViewAudit = hasPerm('console_audit_view');
  const { taskId } = useParams<{ taskId?: string }>();
  const { show } = useSnackbar();
  const { markAllRead } = useBgTaskReadState();

  // Visiting the page marks everything finished-so-far as read, clearing the
  // notification-bell badge (shared localStorage sweep key).
  useEffect(() => {
    markAllRead();
  }, [markAllRead]);

  // A live monitoring page: polls from the start. Owned here (not by the
  // grid) so loading a preset — which remounts the grid — keeps the period.
  // The default is not audited; a period the user picks is.
  const autoRefresh = useGridAutoRefresh({ grid: 'bgtasks', defaultIntervalMs: 10_000 });

  const presets = useGridPresets({
    grid: 'bgtasks',
    defaultOrder: FIELD_ORDER,
    defaultHidden: DEFAULT_HIDDEN,
    defaultOrdering: '-created_ts',
    filters: PRESET_FILTERS,
  });

  // URL-backed filters shared by the header row and the toolbar (project grid
  // rule). Task type / status / visibility / user get header filters; tenant
  // and the started range are toolbar-only. A loaded preset supplies filter
  // values through the preset layer.
  const {
    filterModel,
    onFilterModelChange,
    extra,
    getParam,
    setParam,
    setParams,
    resetAll: resetFilters,
    anyActive: hasFilters,
  } = useGridFilters(FILTERS, presets.filtersLayer);

  const typeOptions = useQuery({
    queryKey: ['bgtask-types'],
    queryFn: () => bgtasksApi.taskTypes(),
    staleTime: 5 * 60_000,
  });

  // Toolbar display values reconstructed from the URL params. The tenant filter:
  // the operators the effective param names (URL > preset > scope; the picker
  // resolves their names), or exactly the Console-only sentinel.
  const tourOperatorId = getParam('tour_operator_id');
  const scopeList = useTenantScopeList();
  const tenants: TenantPickerValue[] =
    tourOperatorId?.trim() === 'null'
      ? [CONSOLE_TENANT]
      : pageTenantsFrom(tourOperatorId, scopeList);
  /** Console only → `null`, else the id list (none clears the filter). */
  const setTenants = (next: TenantPickerValue[]) =>
    setParam(
      'tour_operator_id',
      pickedSentinel(next, CONSOLE_EXTRA_OPTIONS) ? 'null' : formatTenantIds(next.map((t) => t.id)),
    );
  const userParam = getParam('user');
  const user: UserPickerValue | null =
    userParam && /^\d+$/.test(userParam)
      ? { id: Number(userParam), email: `user #${userParam}` }
      : null;
  const taskType = getParam('task_type') ?? '';
  const states = (getParam('state') ?? '').split(',').filter(Boolean).map(Number);
  // ISO instants or relative tokens (`now-1h`): the backend resolves a token per request.
  const startedFrom = paramToBound(getParam('started_from'));
  const startedTo = paramToBound(getParam('started_to'), { end: true });

  const copyText = (text: string, message: string) => {
    void navigator.clipboard
      .writeText(text)
      .then(() => show(message, 'success'))
      .catch(() => show('Could not copy to clipboard.', 'error'));
  };

  const columns = useMemo<GridColDef<BackgroundTaskRecord>[]>(() => {
    const detailsAction = (id: string): CellAction => ({
      key: 'details',
      label: 'Open task details',
      icon: <OpenInNewIcon fontSize="small" />,
      onClick: () => navigate(`/bgtasks/${id}`),
    });
    const shareAction = (id: string): CellAction => ({
      key: 'share',
      label: 'Copy link to this task',
      icon: <ShareIcon fontSize="small" />,
      onClick: () =>
        copyText(`${window.location.origin}/bgtasks/${id}`, 'Task link copied to clipboard.'),
    });
    // Every audit row this run produced: the launch, approvals/cancels, and
    // the per-record rows the task wrote (batch.object.*, tags.changed, …).
    const auditTrailActions = (id: string): CellAction[] =>
      canViewAudit
        ? [
            {
              key: 'audit-trail',
              label: 'View audit trail',
              icon: <HistoryIcon fontSize="small" />,
              onClick: () => navigate(`/audit?task_id=${id}`),
            },
          ]
        : [];

    return [
      {
        field: 'id',
        headerName: 'ID',
        width: 130,
        sortable: false,
        filterable: false,
        renderCell: (p) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <CellActionsPopover
              auto
              title={p.row.id}
              actions={[
                detailsAction(p.row.id),
                shareAction(p.row.id),
                ...auditTrailActions(p.row.id),
                {
                  key: 'copy-id',
                  label: 'Copy task ID',
                  icon: <ContentCopyIcon fontSize="small" />,
                  onClick: () => copyText(p.row.id, 'Task ID copied to clipboard.'),
                },
              ]}
            >
              <Box component="code" sx={{ fontSize: 12 }}>
                {p.row.id.slice(0, 8)}
              </Box>
            </CellActionsPopover>
            <Tooltip title="Copy full task ID">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation(); // don't open the row's details dialog
                  copyText(p.row.id, 'Task ID copied to clipboard.');
                }}
              >
                <ContentCopyIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
      {
        field: 'tour_operator',
        headerName: 'Tenant',
        width: 170,
        // Toolbar autocomplete covers this — a free-text header filter can't
        // resolve names to IDs server-side.
        filterable: false,
        valueGetter: (_v, row) => row.tour_operator?.name ?? 'Console',
        renderCell: (p) => {
          const to = p.row.tour_operator;
          if (!to) {
            return (
              <CellActionsPopover
                auto
                actions={[
                  detailsAction(p.row.id),
                  {
                    key: 'filter-console',
                    label: 'Show console-scoped tasks only',
                    icon: <FilterAltIcon fontSize="small" />,
                    onClick: () => setTenants([CONSOLE_TENANT]),
                  },
                ]}
              >
                Console
              </CellActionsPopover>
            );
          }
          return (
            <WithTenantMark environment={to.environment}>
              <CellActionsPopover
                auto
                actions={[
                  detailsAction(p.row.id),
                  {
                    key: 'filter-tenant',
                    label: `Filter by tenant "${to.name}"`,
                    icon: <FilterAltIcon fontSize="small" />,
                    // Replaces the filter with this one tenant.
                    onClick: () => setTenants([to]),
                  },
                  {
                    key: 'open-to',
                    label: 'Open tour operator',
                    icon: <OpenInNewIcon fontSize="small" />,
                    href: `/tour-operators/${to.id}`,
                  },
                ]}
              >
                {to.name}
              </CellActionsPopover>
            </WithTenantMark>
          );
        },
      },
      {
        field: 'task_label',
        headerName: 'Title',
        flex: 1,
        minWidth: 260,
        filterable: true,
        filterOperators: containsOnly,
      },
      {
        field: 'task_type',
        headerName: 'Type',
        width: 190,
        type: 'singleSelect',
        valueOptions: (typeOptions.data?.results ?? []).map((t) => ({
          value: t.value,
          label: t.label,
        })),
        filterOperators: isOperator,
        renderCell: (p) => <code style={{ fontSize: 12 }}>{p.row.task_type}</code>,
      },
      {
        field: 'state',
        headerName: 'Status',
        width: 170,
        type: 'singleSelect',
        valueOptions: STATE_OPTIONS.map((o) => ({ value: String(o.value), label: o.label })),
        filterOperators: isOperator,
        renderCell: (p) => <TaskStateChip task={p.row} />,
      },
      {
        field: 'visibility',
        headerName: 'Visibility',
        width: 130,
        type: 'singleSelect',
        valueOptions: VISIBILITY_OPTIONS,
        filterOperators: isOperator,
        valueGetter: (_v, row) => row.visibility_display,
      },
      {
        field: 'progress',
        headerName: 'Progress',
        width: 130,
        sortable: false,
        filterable: false,
        renderCell: (p) => {
          const pr = p.row.progress;
          const duration = formatDuration(p.row.duration_seconds);
          const caption = [pr?.step, duration !== '—' ? duration : ''].filter(Boolean).join(' · ');
          if (!pr || (!pr.total && !pr.step)) return caption || '—';
          return (
            <Box sx={{ width: '100%' }}>
              <TaskProgressBar progress={pr} />
              {caption && (
                <Typography variant="caption" color="text.secondary" noWrap display="block">
                  {caption}
                </Typography>
              )}
            </Box>
          );
        },
      },
      {
        field: 'user_email',
        headerName: 'User',
        width: 200,
        filterable: true,
        filterOperators: containsOnly,
        renderCell: (p) => {
          if (!p.row.user_email && !p.row.user_id) return '—';
          const email = p.row.user_email ?? `#${String(p.row.user_id)}`;
          return (
            <CellActionsPopover
              auto
              actions={[
                detailsAction(p.row.id),
                ...(p.row.user_id
                  ? [
                      {
                        key: 'filter-user',
                        label: `Filter by user "${email}"`,
                        icon: <FilterAltIcon fontSize="small" />,
                        onClick: () => setParam('user', String(p.row.user_id)),
                      },
                      {
                        key: 'open-user',
                        label: 'Open user profile',
                        icon: <OpenInNewIcon fontSize="small" />,
                        href: `/users/${p.row.user_id}`,
                      },
                    ]
                  : []),
              ]}
            >
              {email}
            </CellActionsPopover>
          );
        },
      },
      {
        field: 'started_ts',
        headerName: 'Started',
        width: 210,
        filterable: false,
        renderCell: (p) => {
          if (!p.row.started_ts) return '—';
          const ts = new Date(p.row.started_ts).getTime();
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.4 }}>
              <span>
                <CellActionsPopover
                  auto
                  title={utcTs(p.row.started_ts)}
                  actions={[
                    detailsAction(p.row.id),
                    ...DELTAS.map(({ label, tooltip, ms }) => ({
                      key: label,
                      label,
                      tooltip: `Filter Started to ${tooltip} around this timestamp`,
                      onClick: () =>
                        setParams({
                          started_from: new Date(ts - ms).toISOString(),
                          started_to: new Date(ts + ms).toISOString(),
                        }),
                    })),
                    {
                      key: 'reset',
                      label: 'Reset Started filter',
                      icon: <FilterAltOffIcon fontSize="small" />,
                      onClick: () => setParams({ started_from: null, started_to: null }),
                    },
                  ]}
                >
                  {fmtTs(p.row.started_ts)}
                </CellActionsPopover>
              </span>
              <Typography
                component="span"
                variant="caption"
                color="text.secondary"
                sx={{ lineHeight: 1.4 }}
              >
                {relativeTime(p.row.started_ts)}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'created_ts',
        headerName: 'Created',
        width: 200,
        filterable: false,
        renderCell: (p) => (
          <CellActionsPopover
            auto
            title={utcTs(p.row.created_ts)}
            actions={[detailsAction(p.row.id)]}
          >
            {fmtTs(p.row.created_ts)}
          </CellActionsPopover>
        ),
      },
      {
        // Heartbeat health — flags RUNNING tasks that have gone quiet (stuck/lost)
        // without opening each one. Renders nothing for healthy/finished tasks.
        field: 'health',
        headerName: 'Health',
        width: 110,
        sortable: false,
        filterable: false,
        renderCell: (p) => {
          const h = p.row.health;
          if (!h || h.status === 'alive') return null;
          return (
            <Tooltip title={h.last_heartbeat_ts ? `Last heartbeat: ${fmtTs(h.last_heartbeat_ts)}` : 'No recent heartbeat'}>
              <Typography component="span" variant="caption" color="warning.main">
                {h.status === 'lost' ? 'stuck' : h.status}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        // Failure reason inline (already serialized) — triage failed tasks without
        // opening the detail dialog. Default-hidden; enable via the column manager.
        field: 'error_message',
        headerName: 'Error',
        flex: 1,
        minWidth: 200,
        sortable: false,
        filterable: false,
        renderCell: (p) =>
          p.row.error_message ? (
            <Tooltip title={p.row.error_message}>
              <Typography component="span" variant="body2" color="error.main" noWrap>
                {p.row.error_message}
              </Typography>
            </Tooltip>
          ) : (
            '—'
          ),
      },
      {
        field: 'finished_ts',
        headerName: 'Finished',
        width: 200,
        sortable: false,
        filterable: false,
        renderCell: (p) => (p.row.finished_ts ? fmtTs(p.row.finished_ts) : '—'),
      },
      {
        // Last activity — also an indexed backend sort ((tour_operator, -updated_ts)).
        field: 'updated_ts',
        headerName: 'Updated',
        width: 200,
        filterable: false,
        renderCell: (p) => (p.row.updated_ts ? fmtTs(p.row.updated_ts) : '—'),
      },
      {
        field: 'params',
        headerName: 'Arguments',
        flex: 1,
        minWidth: 220,
        sortable: false,
        filterable: false,
        renderCell: (p) => {
          if (!p.row.params) return '—';
          const summary = paramsSummary(p.row.params);
          const entries = paramsEntries(p.row.params);
          if (summary === null || entries === null) {
            // Unparseable shape — plain text fallback.
            return (
              <Box component="code" sx={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                {JSON.stringify(p.row.params)}
              </Box>
            );
          }
          if (!entries.length) return '—';
          return (
            <CellActionsPopover
              auto
              title={
                <Box component="dl" sx={{ m: 0, fontSize: 12 }}>
                  {entries.map(([key, value]) => (
                    <Box key={key} sx={{ display: 'flex', gap: 0.75, py: 0.1 }}>
                      <Box component="dt" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {key}:
                      </Box>
                      <Box
                        component="dd"
                        sx={{ m: 0, fontFamily: 'monospace', wordBreak: 'break-word' }}
                      >
                        {value}
                      </Box>
                    </Box>
                  ))}
                </Box>
              }
              actions={[
                {
                  key: 'copy-json',
                  label: 'Copy arguments as JSON',
                  icon: <ContentCopyIcon fontSize="small" />,
                  onClick: () =>
                    copyText(JSON.stringify(p.row.params, null, 2), 'Arguments copied.'),
                },
              ]}
            >
              <Typography variant="body2" noWrap>
                {summary}
              </Typography>
            </CellActionsPopover>
          );
        },
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewAudit, navigate, setParam, setParams, typeOptions.data]);

  const listParams = (params: ResourceDataGridParams) => ({
    ordering: params.ordering ?? presets.ordering,
    search: params.search,
    ...(params.extra as Record<string, never>),
  });

  const queryFn = (params: ResourceDataGridParams) =>
    bgtasksApi.list({
      ...listParams(params),
      page: params.page,
      page_size: params.pageSize,
      ...(params.countMode ? { count_mode: params.countMode } : {}),
    });

  const countOnly = (params: ResourceDataGridParams) => bgtasksApi.count(listParams(params));

  // Curated toolbar filters. Tenant / Status / Started stay inline longest;
  // Type / User collapse first (also reachable in the header row). Every
  // filter dimension is also editable via the header-row filters and shares
  // the same URL state.
  const toolbarItems: FilterBarItem[] = [
    {
      key: 'tenant',
      priority: 5,
      active: tenants.length > 0,
      node: (
        <Box sx={{ width: 250 }}>
          {/* Console-scoped tasks are a first-class choice in the same dropdown. */}
          <TenantPicker
            multiple
            fieldWidth={250}
            value={tenants}
            onChange={setTenants}
            extraOptions={CONSOLE_EXTRA_OPTIONS}
          />
        </Box>
      ),
    },
    {
      key: 'status',
      priority: 4,
      active: states.length > 0,
      node: (
        <TextField
          select
          size="small"
          label="Status"
          value={states}
          onChange={(e) =>
            setParam('state', (e.target.value as unknown as number[]).join(',') || null)
          }
          sx={{ width: 180 }}
          SelectProps={{
            multiple: true,
            renderValue: (sel) =>
              (sel as number[])
                .map((v) => STATE_OPTIONS.find((o) => o.value === v)?.label ?? v)
                .join(', '),
          }}
        >
          {STATE_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              <Checkbox size="small" checked={states.includes(o.value)} sx={{ py: 0 }} />
              <ListItemText primary={o.label} />
            </MenuItem>
          ))}
        </TextField>
      ),
    },
    {
      key: 'started',
      priority: 3,
      active: startedFrom !== null || startedTo !== null,
      node: (
        <TimeWindowPicker
          label="Started"
          allowOpenEnded
          value={[startedFrom, startedTo]}
          onChange={([start, end]) =>
            setParams({ started_from: boundToParam(start), started_to: boundToParam(end) })
          }
        />
      ),
    },
    {
      key: 'type',
      priority: 2,
      active: Boolean(taskType),
      node: (
        <TextField
          select
          size="small"
          label="Type"
          value={taskType}
          onChange={(e) => setParam('task_type', e.target.value || null)}
          sx={{ width: 200 }}
        >
          <MenuItem value="">
            <em>Any</em>
          </MenuItem>
          {(typeOptions.data?.results ?? []).map((t) => (
            <MenuItem key={t.value} value={t.value}>
              {t.label}
            </MenuItem>
          ))}
        </TextField>
      ),
    },
    {
      key: 'user',
      priority: 1,
      active: user !== null,
      node: (
        <Box sx={{ width: 230 }}>
          <UserPicker value={user} onChange={(u) => setParam('user', u ? String(u.id) : null)} />
        </Box>
      ),
    },
  ];

  return (
    <Box sx={PAGE_FILL_SX}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Background Tasks <HelpLink story="ui-background-tasks" />
      </Typography>

      <ResponsiveFilterBar items={toolbarItems} />

      {presets.blocking ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <ResourceDataGrid<BackgroundTaskRecord>
          queryKey={['bgtasks']}
          queryFn={queryFn}
          countMode={{ grid: 'bgtasks', countOnly }}
          columns={orderColumns(columns, presets.orderedFields)}
          extra={extra}
          autoRefresh={autoRefresh}
          filterModel={filterModel}
          onFilterModelChange={onFilterModelChange}
          sortModel={presets.sortModel}
          onSortModelChange={presets.onSortModelChange}
          columnVisibilityModel={presets.columnVisibilityModel}
          onColumnVisibilityModelChange={presets.onColumnVisibilityModelChange}
          onColumnOrderChange={presets.onColumnOrderChange}
          headerToolbar={{
            onResetAllFilters: resetFilters,
            resetDisabled: !hasFilters,
            search: true,
            searchPlaceholder: 'Title or task ID',
            exportFileName: 'background-tasks',
            presets: presets.toolbar,
          }}
          onRowClick={(id) => navigate(`/bgtasks/${String(id)}`)}
        />
      )}

      {taskId && <TaskDetailsDialog taskId={taskId} onClose={() => navigate('/bgtasks')} />}
    </Box>
  );
}
